/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Company } from 'types/entities/Company'

/** Scalar / write fields accepted by POST /admin/companies/create. */
const COMPANY_WRITE_FIELDS = new Set([
    'id',
    'name',
    'cin',
    'first_name',
    'last_name',
    'street_address',
    'city',
    'postal_code',
    'state',
    'country',
    'billing_info',
    'business_category',
    'phone',
    'email',
    'notes',
    'registration_number',
    'tax_number',
    'vat_number',
    'is_vat_payer',
    'website',
    'referral_id',
    'iban',
    'swift',
    'default_address_id',
    'default_contact_id',
    'parent_company_id',
    'status',
    'type',
    'branch',
    'employee_size',
    'employee_count',
    'origin',
    'tags',
    'seller_id',
    'hall_id',
    'is_newsletter_enabled',
])

/** Backend validates these as strings; API responses often return 0/1 integers. */
const STRING_FLAG_FIELDS = new Set(['is_vat_payer'])

/**
 * Strip nested relations and coerce API response scalars so create/update
 * payloads match backend validation (e.g. is_vat_payer must be a string).
 */
export function sanitizeCompanyPayload<T extends Partial<Company>>(data: T): Partial<Company> {
    const result: Record<string, unknown> = {}

    for (const key of Object.keys(data)) {
        if (!COMPANY_WRITE_FIELDS.has(key)) continue

        const value = (data as Record<string, unknown>)[key]
        if (value === undefined) continue

        if (STRING_FLAG_FIELDS.has(key) && value !== null) {
            result[key] = String(value)
            continue
        }

        result[key] = value
    }

    return result as Partial<Company>
}
